import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Form, Spinner, Alert, ListGroup } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { mcpTokenActions } from "../../actions/mcpToken.actions";
import { clientActions } from "../../actions/client.actions";
import { clientConstants } from "../../constants/client.constants";

function McpTokenModal({ show, onHide, mode, token }) {
  const dispatch = useDispatch();
  const mcpTokenState = useSelector((state) => state.mcpToken);
  const clientState = useSelector((state) => state.client);

  const [formData, setFormData] = useState({
    clientId: "",
    clientGuid: "",
    clientName: "",
    isActive: true,
    regenerateToken: false,
  });
  const [errors, setErrors] = useState({});

  // Search state
  const [searchText, setSearchText] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch clients when modal opens
  useEffect(() => {
    if (show) {
      dispatch(clientActions.get({ max_results: 500 }, clientConstants.CALLER_CLIENT_GRID));
    }
  }, [show, dispatch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (mode === "edit" && token) {
      // Find the client name from the clients list
      const client = clientState.paginatedResultGrid?.data?.find(
        (c) => c.clientId === token.clientId
      );
      setFormData({
        clientId: token.clientId || "",
        clientGuid: token.clientGuid || "",
        clientName: client?.clientName || token.clientId || "",
        isActive: token.isActive ?? true,
        regenerateToken: false,
      });
      setSearchText(client ? `${client.clientName} (${client.clientId})` : token.clientId || "");
    } else {
      setFormData({
        clientId: "",
        clientGuid: "",
        clientName: "",
        isActive: true,
        regenerateToken: false,
      });
      setSearchText("");
    }
    setErrors({});
  }, [mode, token, show, clientState.paginatedResultGrid?.data]);

  const handleClientSelect = (client) => {
    setFormData({
      ...formData,
      clientId: client.clientId,
      clientGuid: client.clientGuid,
      clientName: client.clientName,
    });
    setSearchText(`${client.clientName} (${client.clientId})`);
    setShowDropdown(false);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    setShowDropdown(true);

    // Clear selection if user is typing
    if (formData.clientId) {
      setFormData({
        ...formData,
        clientId: "",
        clientGuid: "",
        clientName: "",
      });
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.clientId) {
      newErrors.clientId = "Client is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    if (mode === "edit") {
      const updateBody = {
        clientGuid: formData.clientGuid,
        isActive: formData.isActive,
      };
      if (formData.regenerateToken) {
        updateBody.regenerateToken = true;
      }
      dispatch(mcpTokenActions.update(token.clientId, updateBody));
    } else {
      dispatch(mcpTokenActions.create({
        clientId: formData.clientId,
        clientGuid: formData.clientGuid,
        isActive: formData.isActive,
      }));
    }
  };

  const isLoading = mcpTokenState.creating || mcpTokenState.updating;
  const clients = clientState.paginatedResultGrid?.data || [];

  // Filter clients based on search text
  const filteredClients = clients.filter((client) => {
    const search = searchText.toLowerCase();
    return (
      client.clientName?.toLowerCase().includes(search) ||
      client.clientId?.toLowerCase().includes(search)
    );
  });

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{mode === "edit" ? "Edit MCP Token" : "Create MCP Token"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {mcpTokenState.error && (
          <Alert variant="danger">
            {mode === "edit" ? "Failed to update token." : "Failed to create token."}
          </Alert>
        )}

        <Form.Group className="mb-3">
          <Form.Label>Client</Form.Label>
          {clientState.loadingGrid ? (
            <div className="d-flex align-items-center">
              <Spinner animation="border" size="sm" className="me-2" />
              <span className="text-muted">Loading clients...</span>
            </div>
          ) : (
            <div ref={dropdownRef} style={{ position: "relative" }}>
              <Form.Control
                type="text"
                value={searchText}
                onChange={handleSearchChange}
                onFocus={() => setShowDropdown(true)}
                isInvalid={!!errors.clientId}
                placeholder="Type to search clients..."
                autoComplete="off"
              />
              <Form.Control.Feedback type="invalid">
                {errors.clientId}
              </Form.Control.Feedback>
              {showDropdown && filteredClients.length > 0 && (
                <ListGroup
                  style={{
                    position: "absolute",
                    zIndex: 1000,
                    width: "100%",
                    maxHeight: "200px",
                    overflowY: "auto",
                    boxShadow: "0 4px 6px rgba(0,0,0,0.1)"
                  }}
                >
                  {filteredClients.slice(0, 10).map((client) => (
                    <ListGroup.Item
                      key={client.clientId}
                      action
                      onClick={() => handleClientSelect(client)}
                      style={{ cursor: "pointer" }}
                      className={formData.clientId === client.clientId ? "active" : ""}
                    >
                      <strong>{client.clientName}</strong>
                      <span className="text-muted ms-2">({client.clientId})</span>
                    </ListGroup.Item>
                  ))}
                  {filteredClients.length > 10 && (
                    <ListGroup.Item className="text-muted text-center" style={{ fontSize: "0.85rem" }}>
                      {filteredClients.length - 10} more results...
                    </ListGroup.Item>
                  )}
                </ListGroup>
              )}
              {showDropdown && searchText && filteredClients.length === 0 && (
                <ListGroup style={{ position: "absolute", zIndex: 1000, width: "100%" }}>
                  <ListGroup.Item className="text-muted">No clients found</ListGroup.Item>
                </ListGroup>
              )}
            </div>
          )}
          {formData.clientGuid && (
            <Form.Text className="text-muted">
              GUID: {formData.clientGuid}
            </Form.Text>
          )}
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Check
            type="checkbox"
            label="Active"
            checked={formData.isActive}
            onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
          />
        </Form.Group>

        {mode === "edit" && (
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Regenerate Token"
              checked={formData.regenerateToken}
              onChange={(e) => setFormData({ ...formData, regenerateToken: e.target.checked })}
            />
            <Form.Text className="text-muted">
              Check this to generate a new token value. The old token will stop working.
            </Form.Text>
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isLoading}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={isLoading}>
          {isLoading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Saving...
            </>
          ) : mode === "edit" ? (
            "Update"
          ) : (
            "Create"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default McpTokenModal;
